import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useKeydown from "@hooks/useKeydown";
import { useSelector, useDispatch } from "react-redux";
import { selectCtrl, setCtrl } from "@app/global";
import { loginUserWithCredentials } from "@server/requests";
import LOCAL_STORAGE from "@utils/localStorage.js";
import {
  InoInput,
  InoButton,
  InoRow,
  InoKeyboard,
  CheckboxItem,
} from "@ino-ui/tv";
import "./styles/VoucherModal.scss";

export default function LoginModal({ isVisible, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [activeField, setActiveField] = useState("username"); // "username", "password", "submit", "close"
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardTarget, setKeyboardTarget] = useState("username");
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const ctrl = useSelector(selectCtrl);

  useEffect(() => {
    if (isVisible) {
      setActiveField("username");
      setError(null);
      setUsername("");
      setPassword("");
      setShowKeyboard(false);
    }
  }, [isVisible]);

  const handleSubmit = async () => {
    if (!username || !password) {
      setError(t("Please enter both username and password"));
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const defaultDeviceInfo = {
        device_id: "45.153.215.91_web",
        device_model: "WebOs",
        device_name: "WebOs",
        auth_sub: true,
      };

      const res = await loginUserWithCredentials({
        username: username,
        password: password,
        ...defaultDeviceInfo,
      });

      const response = JSON.parse(res);

      if (!response.error) {
        LOCAL_STORAGE.TOKEN.SET(response.message);
        onSubmit();
        onClose();
      } else {
        setError(
          response.message || t("Invalid credentials. Please try again.")
        );
      }
    } catch (err) {
      setError(t("An unexpected error occurred. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyboardInput = (character) => {
    if (
      character === "backspace" ||
      character === "delete" ||
      character === "back"
    ) {
      if (keyboardTarget === "username") {
        setUsername((prev) => prev.slice(0, -1));
      } else if (keyboardTarget === "password") {
        setPassword((prev) => prev.slice(0, -1));
      }
      return;
    }

    if (character === "Clear") {
      if (keyboardTarget === "username") {
        setUsername("");
      } else if (keyboardTarget === "password") {
        setPassword("");
      }
      return;
    }

    // For regular characters, append to existing value
    if (character && typeof character === "string" && character.length === 1) {
      if (keyboardTarget === "username") {
        setUsername((prev) => prev + character);
      } else if (keyboardTarget === "password") {
        setPassword((prev) => prev + character);
      }
    }
  };

  const handleKeyboardClose = () => {
    setShowKeyboard(false);
  };

  const handleKeyboardSubmit = () => {
    setShowKeyboard(false);
    if (keyboardTarget === "username") {
      setActiveField("password");
    } else {
      setActiveField("submit");
    }
  };

  const openKeyboard = (target) => {
    setKeyboardTarget(target);
    setShowKeyboard(true);
  };

  useKeydown({
    isActive: isVisible && !showKeyboard,
    up: () => {
      if (activeField === "password") setActiveField("username");
      else if (activeField === "submit") setActiveField("password");
      else if (activeField === "close") setActiveField("submit");
    },
    down: () => {
      if (activeField === "username") setActiveField("password");
      else if (activeField === "password") setActiveField("submit");
      else if (activeField === "submit") setActiveField("close");
    },
    left: () => {
      if (activeField === "submit" || activeField === "close") {
        setActiveField(activeField === "close" ? "submit" : "close");
      }
    },
    right: () => {
      if (activeField === "submit" || activeField === "close") {
        setActiveField(activeField === "submit" ? "close" : "submit");
      }
    },
    ok: () => {
      if (activeField === "username") {
        openKeyboard("username");
      } else if (activeField === "password") {
        openKeyboard("password");
      } else if (activeField === "submit") {
        handleSubmit();
      } else if (activeField === "close") {
        onClose();
      }
    },
    back: () => {
      onClose();
    },
  });

  if (!isVisible) return null;

  return (
    <div className={`voucher-modal-overlay ${isVisible ? "visible" : ""}`}>
      <div className="voucher-modal">
        <div className="voucher-header">
          <h2 className="voucher-modal-title">{t("Login")}</h2>
          {/* <p>{t("Enter your username and password")}</p> */}
        </div>

        <div className="voucher-content">
          <div className="login-fields-ino">
            <div className="login-field-ino">
              <InoInput
                value={username}
                isActive={activeField === "username"}
                // onChange={(e) => setUsername(e.target.value)}
                placeholder={t("Enter username")}
                onFocus={() => openKeyboard("username")}
                onBlur={() => setActiveField("username")}
                classNames="login-input-text"
                showCursor={username.length === 0}
              />
            </div>

            <div className="login-field-ino">
              <InoInput
                value={password}
                isActive={activeField === "password"}
                type={showPassword ? "text" : "password"}
                // onChange={(e) => setPassword(e.target.value)}
                placeholder={t("Enter password")}
                onFocus={() => openKeyboard("password")}
                onBlur={() => setActiveField("password")}
                classNames="login-input-text"
                showCursor={password.length === 0}
                onDown={() => {
                  if (!showKeyboard) {
                    setActiveField("show-password");
                  }
                }}
              />
            </div>

            {/* show password */}
            <div className="login-show-password">
              <CheckboxItem
                isActive={activeField === "show-password"}
                classNames="login-show-password-checkbox"
                label={showPassword ? t("Hide password") : t("Show password")}
                onUp={() => setActiveField("password")}
                onChange={() => setShowPassword(!showPassword)}
                isRTL={true}
                onDown={() => {
                  if (!showKeyboard) {
                    setActiveField("submit");
                  }
                }}
              />
            </div>
          </div>

          {error && (
            <div className="voucher-error">
              <p>{error}</p>
            </div>
          )}

          <InoRow
            isActive={activeField === "submit" || activeField === "close"}
            classNames="login-actions-row"
          >
            <InoButton
              isActive={activeField === "submit"}
              disabled={isLoading}
              onClick={handleSubmit}
              size="large"
              classNames="login-submit-btn"
              onUp={() => {
                if (!showKeyboard) {
                  setActiveField("show-password");
                }
              }}
            >
              {isLoading ? t("Logging in...") : t("Login")}
            </InoButton>
            <InoButton
              isActive={activeField === "close"}
              onClick={onClose}
              onUp={() => {
                if (!showKeyboard) {
                  setActiveField("show-password");
                }
              }}
              variant="outline"
              size="large"
              classNames="login-cancel-btn"
            >
              {t("Cancel")}
            </InoButton>
          </InoRow>
        </div>
      </div>

      {showKeyboard && (
        <InoKeyboard
          key={`keyboard-${keyboardTarget}-${showKeyboard ? "open" : "closed"}`}
          isOpen={showKeyboard}
          value={keyboardTarget === "username" ? username : password}
          onChange={handleKeyboardInput}
          onClose={handleKeyboardClose}
          onSubmit={handleKeyboardSubmit}
          infinite={true}
          placeholder={
            keyboardTarget === "username" ? t("Username") : t("Password")
          }
          type={keyboardTarget === "password" ? "password" : "text"}
          title={
            keyboardTarget === "username"
              ? t("Enter Username")
              : t("Enter Password")
          }
          classNames="login-keyboard"
        />
      )}
    </div>
  );
}
